import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Eu, Falha } from '../../acesso';
import { podeFazer } from '../../acesso';
import type { Sessao } from '../../auth';
import { SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { lerReais, listarProdutos, reais } from '../../produtos';
import type { ProdutoComercial } from '../../produtos';
import {
  lerCampanha,
  lerCampanhas,
  lerRegras,
  mudarCampanha,
  publicarParametros,
  salvarMetaSemanal,
  salvarCampanha,
  simularRegra,
} from '../../administracao';
import type {
  Campanha,
  DetalheDaCampanha,
  EstadoDaCampanha,
  PublicoDaCampanha,
  RegrasVigentes,
  SimulacaoDaRegra,
  VersaoDeParametro,
} from '../../administracao';
import { ErroDaLista, Esqueleto, useEscape } from '../equipe/Equipe';
import {
  Desfecho,
  IconeExtra,
  deCampoDataHora,
  diaMes,
  diaMesAno,
  diaMesHora,
  paraCampoDataHora,
  periodoCurto,
} from './comunicacao-comum';
import { REGRAS_DA_PREMIACAO } from '../../premiacao';
import { MetaDaEquipeDoAdmin } from './MetaDaEquipe';
import '../equipe/acesso.css';
import './regras.css';

/**
 * TELA 29 — REGRAS E CAMPANHAS. "Regra muda. Histórico não."
 *
 * - As regras vigentes são SÓ LEITURA aqui: publicar versão nova de dinheiro não existe no 2.0
 *   (o servidor devolve `publicacao.permitida = false` com o porquê — decisão do dono).
 * - O simulador roda no SERVIDOR e não cria venda; o resultado vem marcado como simulação.
 * - Campanha é camada temporária sobre produtos existentes e NÃO altera a premiação. O estado
 *   (programada, ativa, encerrada) vem do servidor, que olha o relógio; publicada não se edita;
 *   pausar e cancelar pedem motivo.
 */

type Programa = 'vitamina' | 'pre_vencido' | 'paradao';
type Carga<T> =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto'; readonly dados: T };
type ListaDeCampanhas = {
  campanhas: readonly Campanha[];
  contagem: Readonly<Record<EstadoDaCampanha, number>>;
};
type Filtro = 'todas' | 'ativa' | 'programadas' | 'rascunho' | 'pausadas' | 'encerradas';

const NOME_DO_PROGRAMA: Readonly<Record<Programa, string>> = {
  vitamina: 'Vitaminas',
  pre_vencido: 'Pré-vencidos',
  paradao: 'Paradão',
};
const PUBLICO: Readonly<Record<PublicoDaCampanha, string>> = {
  TODOS: 'Todos',
  BALCONISTAS: 'Balconistas',
  ADMINISTRADORES: 'Administradores',
};
const NO_FILTRO: Readonly<Record<Filtro, readonly EstadoDaCampanha[]>> = {
  todas: ['RASCUNHO', 'PROGRAMADA', 'ATIVA', 'PAUSADA', 'ENCERRADA', 'CANCELADA'],
  ativa: ['ATIVA'],
  programadas: ['PROGRAMADA'],
  rascunho: ['RASCUNHO'],
  pausadas: ['PAUSADA'],
  encerradas: ['ENCERRADA', 'CANCELADA'],
};

export function RegrasECampanhas({
  sessao,
  eu,
  parametros,
  aba,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
  aba: 'regras' | 'campanhas';
}) {
  const [regras, setRegras] = useState<Carga<RegrasVigentes>>({ fase: 'carregando' });
  const [lista, setLista] = useState<Carga<ListaDeCampanhas>>({ fase: 'carregando' });
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [regraAberta, setRegraAberta] = useState<Programa | null>(null);
  const [historico, setHistorico] = useState(false);
  const [formulario, setFormulario] = useState<{ id: string | null } | null>(null);
  const [pausando, setPausando] = useState<Campanha | null>(null);
  const cartaoDeCampanhas = useRef<HTMLDivElement>(null);
  const gerir = podeFazer(eu, 'campanhas.gerenciar');
  const simula = gerir || podeFazer(eu, 'produtos.configurar');
  const aberta = parametros.get('campanha');

  const carregarRegras = useCallback(async () => {
    const r = await lerRegras();
    setRegras(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, []);
  const carregarCampanhas = useCallback(async () => {
    const r = await lerCampanhas();
    setLista(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, []);
  useEffect(() => {
    void carregarRegras();
    void carregarCampanhas();
  }, [carregarRegras, carregarCampanhas]);
  useEffect(() => {
    if (aba === 'campanhas') cartaoDeCampanhas.current?.scrollIntoView({ block: 'nearest' });
  }, [aba]);

  const abrirCampanha = (id: string | null) => {
    window.location.hash = id ? `#/${aba}?campanha=${id}` : `#/${aba}`;
  };

  if (
    (regras.fase === 'erro' && regras.falha.tipo === 'sem-acesso') ||
    (lista.fase === 'erro' && lista.falha.tipo === 'sem-acesso')
  )
    return <SemAcesso area="Regras e campanhas" />;

  const r = regras.fase === 'pronto' ? regras.dados : null;
  const dados = lista.fase === 'pronto' ? lista.dados : null;
  const contagem = dados?.contagem;
  const visiveis = dados?.campanhas.filter((c) => NO_FILTRO[filtro].includes(c.estado)) ?? [];
  const ultima = dados?.campanhas.find((c) => c.estado === 'RASCUNHO') ?? dados?.campanhas[0];
  const chips: readonly (readonly [Filtro, string, number])[] = contagem
    ? [
        ['todas', 'Todas', dados.campanhas.length],
        ['ativa', 'Ativa', contagem.ATIVA],
        ['programadas', 'Programadas', contagem.PROGRAMADA],
        ['rascunho', 'Rascunho', contagem.RASCUNHO],
        ...(contagem.PAUSADA > 0
          ? [['pausadas', 'Pausadas', contagem.PAUSADA] as const]
          : ([] as const)),
        ['encerradas', 'Encerradas', contagem.ENCERRADA + contagem.CANCELADA],
      ]
    : [];

  return (
    <div className="ac ac-cheia rg">
      <div className="ac-cabeca">
        <div>
          <h1 className="ac-titulo">Regras vigentes</h1>
          <div className="ac-subtitulo">
            Regra efetiva resolvida pelo sistema · vendas ficam ligadas à versão vigente no momento
            do registro
          </div>
        </div>
        <div className="ac-acoes">
          <button type="button" className="ac-botao" onClick={() => setHistorico(true)}>
            Histórico de versões
          </button>
          {gerir && (
            <button
              type="button"
              className="ac-botao ac-botao-primario"
              onClick={() => setFormulario({ id: null })}
            >
              <Icone nome="plus" tamanho={14} traco={1.8} />
              Nova campanha
            </button>
          )}
        </div>
      </div>

      {regras.fase === 'erro' ? (
        <ErroDaLista
          titulo="Não foi possível carregar as regras vigentes"
          texto="As campanhas abaixo continuam visíveis."
          aoTentar={() => void carregarRegras()}
        />
      ) : (
        <div className="rg-cartoes">
          <CartaoDeRegra
            icone={<Icone nome="money" tamanho={15} traco={1.7} />}
            programa="vitamina"
            r={r}
            aoAbrir={() => setRegraAberta('vitamina')}
          />
          <CartaoDeRegra
            icone={<Icone nome="clock" tamanho={15} traco={1.7} />}
            programa="pre_vencido"
            r={r}
            aoAbrir={() => setRegraAberta('pre_vencido')}
          />
          <CartaoDeRegra
            icone={<Icone nome="box" tamanho={15} traco={1.7} />}
            programa="paradao"
            r={r}
            aoAbrir={() => setRegraAberta('paradao')}
          />
          <CartaoDaCampanha
            c={ultima ?? null}
            carregando={dados === null}
            aoAbrir={() => ultima && abrirCampanha(ultima.id)}
          />
        </div>
      )}

      {r && (
        <details className="ac-cartao rg-como">
          <summary>
            <strong>Como a conta é feita</strong>
            <span>
              meta da semana: {r.metaSemanal.itens} itens · semana de sábado a sexta · as 12 regras
              da conferência
            </span>
          </summary>
          <ol>
            {REGRAS_DA_PREMIACAO.map((x) => (
              <li key={x.titulo}>
                <strong>{x.titulo}</strong> — {x.efeito}
              </li>
            ))}
          </ol>
        </details>
      )}

      {/* Meta da equipe com brinde: quem gerencia campanhas define e vê quem fez quanto. */}
      {gerir && <MetaDaEquipeDoAdmin sessao={sessao} />}

      <div className="ac-cartao ac-cartao-tabela rg-campanhas" ref={cartaoDeCampanhas}>
        <div className="rg-campanhas-cabeca">
          <h2 className="rg-campanhas-titulo">Campanhas</h2>
          <span className="rg-camada">camada temporária · não altera a premiação</span>
          <span className="ac-espaco" />
          <div className="rg-chips" role="group" aria-label="Filtrar campanhas">
            {chips.map(([id, rotulo, n]) => (
              <button
                key={id}
                type="button"
                aria-pressed={filtro === id}
                className={`rg-chip${filtro === id ? ' rg-chip-ativo' : ''}`}
                onClick={() => setFiltro(id)}
              >
                {rotulo} {n}
              </button>
            ))}
          </div>
        </div>
        {lista.fase === 'carregando' && <Esqueleto linhas={5} />}
        {lista.fase === 'erro' && (
          <div style={{ padding: 16 }}>
            <ErroDaLista
              titulo="Não foi possível carregar as campanhas"
              texto="As regras vigentes acima continuam valendo."
              aoTentar={() => void carregarCampanhas()}
            />
          </div>
        )}
        {dados && visiveis.length === 0 && (
          <div className="ac-vazio">
            <div className="ac-vazio-titulo">
              {dados.campanhas.length === 0
                ? 'Nenhuma campanha ainda'
                : 'Nenhuma campanha neste filtro'}
            </div>
            <div className="ac-vazio-texto">As regras por tipo continuam valendo normalmente.</div>
            {gerir && dados.campanhas.length === 0 && (
              <button
                type="button"
                className="ac-botao"
                style={{ marginTop: 12 }}
                onClick={() => setFormulario({ id: null })}
              >
                <Icone nome="plus" tamanho={14} traco={1.8} />
                Nova campanha
              </button>
            )}
          </div>
        )}
        {visiveis.length > 0 && (
          <div className="ac-tabela-rolo">
            <table className="ac-tabela rg-tabela">
              <thead>
                <tr>
                  <th>CAMPANHA</th>
                  <th>PERÍODO</th>
                  <th className="ac-direita">PRODUTOS</th>
                  <th>PÚBLICO</th>
                  <th className="rg-col-larga">PREMIAÇÃO</th>
                  <th>STATUS</th>
                  <th className="rg-col-larga">RESPONSÁVEL</th>
                  <th aria-label="Ações" />
                </tr>
              </thead>
              <tbody>
                {visiveis.map((c) => (
                  <LinhaDaCampanha
                    key={c.id}
                    c={c}
                    gerir={gerir}
                    aoAbrir={() => abrirCampanha(c.id)}
                    aoContinuar={() => setFormulario({ id: c.id })}
                    aoPausar={() => setPausando(c)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="ac-rodape-tabela">
          <span>
            {dados
              ? `${String(visiveis.length)} de ${String(dados.campanhas.length)} ${dados.campanhas.length === 1 ? 'campanha' : 'campanhas'}`
              : ' '}
          </span>
          <span>Pausar e cancelar preservam as vendas passadas · histórico permanece</span>
        </div>
      </div>

      {regraAberta && r && (
        <DrawerDaRegra
          programa={regraAberta}
          r={r}
          sessao={sessao}
          simula={simula}
          publica={podeFazer(eu, 'configuracoes.administrar')}
          aoMudar={() => void carregarRegras()}
          aoFechar={() => setRegraAberta(null)}
        />
      )}
      {historico && r && (
        <DrawerDoHistorico
          r={r}
          sessao={sessao}
          dono={podeFazer(eu, 'loja.ver')}
          aoMudar={() => void carregarRegras()}
          aoFechar={() => setHistorico(false)}
        />
      )}
      {aberta && (
        <DrawerDaCampanha
          key={aberta}
          id={aberta}
          sessao={sessao}
          gerir={gerir}
          aoFechar={() => abrirCampanha(null)}
          aoEditar={(id) => setFormulario({ id })}
          aoMudar={() => void carregarCampanhas()}
        />
      )}
      {formulario && (
        <FormularioDaCampanha
          id={formulario.id}
          sessao={sessao}
          aoFechar={() => setFormulario(null)}
          aoSalvar={(id) => {
            setFormulario(null);
            void carregarCampanhas();
            abrirCampanha(id);
          }}
          aoConferir={() => {
            setFormulario(null);
            void carregarCampanhas();
          }}
        />
      )}
      {pausando && (
        <ModalDeMotivo
          titulo={`Pausar ${pausando.nome}`}
          texto="Vendas já registradas continuam válidas. A campanha deixa de valer a partir do momento definido pelo servidor."
          botao="Confirmar pausa"
          aoFechar={() => setPausando(null)}
          aoConfirmar={(motivo) =>
            mudarCampanha(sessao, pausando.id, 'pausar', pausando.versao, motivo)
          }
          aoConcluir={() => {
            setPausando(null);
            void carregarCampanhas();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------ cartões de regra ------------------------------ */

function CartaoDeRegra({
  icone,
  programa,
  r,
  aoAbrir,
}: {
  icone: ReactNode;
  programa: Programa;
  r: RegrasVigentes | null;
  aoAbrir: () => void;
}) {
  if (!r) return <div className="rg-regra rg-regra-esqueleto" aria-busy="true" />;
  let versao: string;
  let texto: string;
  let pe: string;
  let ativa: boolean;
  if (programa === 'vitamina') {
    ativa = r.vitamina.produtos > 0;
    versao = r.vitamina.ultimaMudanca
      ? `por produto · desde ${diaMes(r.vitamina.ultimaMudanca.em)}`
      : 'por produto · sem faixa gravada';
    texto = `Premiação por faixa configurada no produto · ${String(r.vitamina.produtos)} ${r.vitamina.produtos === 1 ? 'produto' : 'produtos'} no programa`;
    pe = r.vitamina.ultimaMudanca
      ? `${r.vitamina.ultimaMudanca.por ?? 'sistema'} · ${diaMesHora(r.vitamina.ultimaMudanca.em)}`
      : 'Nenhum produto configurado ainda';
  } else {
    const v = (programa === 'pre_vencido' ? r.preVencido : r.paradao)[0];
    ativa = v !== undefined;
    versao = v
      ? `${programa === 'paradao' ? 'regra do pré-vencido · ' : ''}v${String(v.versao)} · desde ${diaMes(v.desde)}`
      : 'sem versão gravada';
    // os números da regra, como a balconista entende: na base, acima dela e o teto
    texto = v
      ? `Na base: ${r2(v.premioBase)} · acima dela: ${String(Number(v.pctAumento))}% do aumento (mín. ${r2(v.premioBase)}, teto ${r2(v.teto)}) · sem base: R$ 0,00`
      : programa === 'pre_vencido'
        ? 'Lotes elegíveis pela regra de validade vigente'
        : 'Produtos elegíveis pela regra de ausência de vendas vigente';
    pe = v
      ? `${v.por ?? origemLegivel(v.origem)} · ${diaMesHora(v.desde)}`
      : 'Sem parâmetro gravado';
  }
  return (
    <button type="button" className="rg-regra" onClick={aoAbrir}>
      <span className="rg-regra-topo">
        {icone}
        <span className="rg-regra-nome">{NOME_DO_PROGRAMA[programa]}</span>
        <span className={`rg-selo ${ativa ? 'rg-selo-ok' : 'rg-selo-neutro'}`}>
          {ativa ? 'ATIVA' : 'SEM VERSÃO'}
        </span>
      </span>
      <span className="rg-regra-versao">{versao}</span>
      <span className="rg-regra-texto">{texto}</span>
      <span className="rg-regra-pe">{pe}</span>
    </button>
  );
}

function CartaoDaCampanha({
  c,
  carregando,
  aoAbrir,
}: {
  c: Campanha | null;
  carregando: boolean;
  aoAbrir: () => void;
}) {
  if (carregando) return <div className="rg-regra rg-regra-esqueleto" aria-busy="true" />;
  const rascunho = c?.estado === 'RASCUNHO';
  const conteudo = (
    <>
      <span className="rg-regra-topo">
        <IconeExtra nome="tag" tamanho={15} traco={1.7} />
        <span className="rg-regra-nome">Campanha temporária</span>
        <span className="rg-selo rg-selo-aviso">
          {c ? ROTULO_DO_ESTADO[c.estado].toUpperCase() : 'NENHUMA'}
        </span>
      </span>
      <span className="rg-regra-versao">
        {c ? (rascunho ? `${c.nome} · não publicada` : c.nome) : 'nenhuma campanha criada'}
      </span>
      <span className="rg-regra-texto">
        {rascunho
          ? 'Não afeta venda, não aparece para o balconista, não entra em cálculo'
          : 'Camada sobre produtos existentes · não altera a premiação'}
      </span>
      <span className="rg-regra-pe">
        {c
          ? `${c.responsavel ? `${c.responsavel} · ` : ''}${diaMesHora(c.atualizadoEm)}`
          : 'As regras por tipo valem sozinhas'}
      </span>
    </>
  );
  return c ? (
    <button type="button" className="rg-regra rg-regra-campanha" onClick={aoAbrir}>
      {conteudo}
    </button>
  ) : (
    <div className="rg-regra rg-regra-campanha">{conteudo}</div>
  );
}

/** '2.0000' → 'R$ 2,00' (o parâmetro vem com 4 casas do banco). */
const r2 = (v: string) => reais(Number(v).toFixed(2));

function origemLegivel(origem: string): string {
  if (origem === 'migracao_1_0') return 'migração do 1.0';
  if (origem === 'decisao_do_dono') return 'decisão do dono';
  return origem.replace(/_/g, ' ');
}

/* ------------------------------ lista de campanhas ------------------------------ */

const ROTULO_DO_ESTADO: Readonly<Record<EstadoDaCampanha, string>> = {
  RASCUNHO: 'Rascunho',
  PROGRAMADA: 'Programada',
  ATIVA: 'Ativa',
  ENCERRADA: 'Encerrada',
  PAUSADA: 'Pausada',
  CANCELADA: 'Cancelada',
};

export function StatusDaCampanha({ c }: { c: Pick<Campanha, 'estado' | 'inicio'> }) {
  const texto =
    c.estado === 'PROGRAMADA' && c.inicio
      ? `Programada · ${diaMesHora(c.inicio)}`
      : ROTULO_DO_ESTADO[c.estado];
  return (
    <span className={`rg-status rg-status-${c.estado.toLowerCase()}`}>
      {c.estado === 'ATIVA' && <span className="ac-status-ponto" />}
      {texto}
    </span>
  );
}

function LinhaDaCampanha({
  c,
  gerir,
  aoAbrir,
  aoContinuar,
  aoPausar,
}: {
  c: Campanha;
  gerir: boolean;
  aoAbrir: () => void;
  aoContinuar: () => void;
  aoPausar: () => void;
}) {
  const passada = c.estado === 'ENCERRADA' || c.estado === 'CANCELADA';
  const sub =
    c.estado === 'CANCELADA'
      ? 'cancelada · motivo registrado'
      : c.estado === 'RASCUNHO' && !c.descricao
        ? 'em elaboração'
        : c.descricao;
  return (
    <tr className={passada ? 'rg-passada' : undefined}>
      <td>
        <div className="rg-nome">{c.nome}</div>
        {sub && (
          <div className={`rg-sub${c.estado === 'CANCELADA' ? ' rg-sub-cancelada' : ''}`}>
            {sub}
          </div>
        )}
      </td>
      <td className={`ac-num${c.inicio ? '' : ' ac-fraco'}`}>{periodoCurto(c.inicio, c.fim)}</td>
      <td className="ac-direita ac-num">{c.produtos}</td>
      <td className={c.estado === 'RASCUNHO' ? 'ac-fraco' : 'ac-cinza'}>
        {c.estado === 'RASCUNHO' && !c.inicio ? '—' : PUBLICO[c.publico]}
      </td>
      <td className="ac-cinza rg-col-larga">não altera</td>
      <td>
        <StatusDaCampanha c={c} />
      </td>
      <td className="ac-cinza rg-col-larga">{c.responsavel ?? '—'}</td>
      <td className="ac-direita">
        <span className="rg-acoes">
          {gerir && c.estado === 'ATIVA' && (
            <button type="button" className="rg-acao-neutra" onClick={aoPausar}>
              Pausar
            </button>
          )}
          {gerir && c.estado === 'RASCUNHO' ? (
            <button type="button" className="ac-link" onClick={aoContinuar}>
              Continuar
            </button>
          ) : passada ? (
            <button type="button" className="rg-acao-neutra rg-acao-fraca" onClick={aoAbrir}>
              Ver
            </button>
          ) : (
            <button type="button" className="ac-link" onClick={aoAbrir}>
              Abrir
            </button>
          )}
        </span>
      </td>
    </tr>
  );
}

/* ------------------------------ drawer da regra (B) ------------------------------ */

function Veu({
  aoFechar,
  children,
  rotulo,
  largo = false,
}: {
  aoFechar: () => void;
  children: ReactNode;
  rotulo: string;
  largo?: boolean;
}) {
  useEscape(aoFechar, true);
  return (
    <div
      className="ac-veu"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <aside
        className={`ac-drawer rg-drawer${largo ? ' rg-drawer-largo' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={rotulo}
      >
        {children}
      </aside>
    </div>
  );
}

function Fechar({ aoFechar }: { aoFechar: () => void }) {
  return (
    <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
      <Icone nome="x" tamanho={15} traco={1.7} />
    </button>
  );
}

const pct = (v: string) => `${v.replace(/\.?0+$/, '').replace('.', ',')}%`;
/** Paradão = a MESMA regra do pré-vencido (dono, 27/09/2026): sem % nem teto próprios. */
const SEGUE_O_PRE_VENCIDO =
  'O paradão segue a regra do pré-vencido (decisão do dono, 27/09/2026): mesmo valor-base, mesmo % e mesmo teto. Para mudar, publique no pré-vencido.';
const tetoLegivel = (v: string) => (Number(v) > 0 ? r2(v) : 'sem teto configurado');

function DrawerDaRegra({
  programa,
  r,
  sessao,
  simula,
  publica,
  aoMudar,
  aoFechar,
}: {
  programa: Programa;
  r: RegrasVigentes;
  sessao: Sessao;
  simula: boolean;
  publica: boolean;
  aoMudar: () => void;
  aoFechar: () => void;
}) {
  const versoes =
    programa === 'pre_vencido' ? r.preVencido : programa === 'paradao' ? r.paradao : [];
  const v = versoes[0];
  return (
    <Veu aoFechar={aoFechar} rotulo={`Regra de ${NOME_DO_PROGRAMA[programa]}`} largo>
      <div className="ac-drawer-cabeca">
        <div style={{ flex: 1 }}>
          <div className="rg-drawer-titulo">
            <span className="ac-drawer-nome">Regra de {NOME_DO_PROGRAMA[programa]}</span>
            {programa === 'vitamina' ? (
              <span className="rg-selo rg-selo-ok rg-selo-g">
                {r.vitamina.produtos > 0 ? 'ATIVA · por produto' : 'SEM PRODUTOS'}
              </span>
            ) : (
              <span className={`rg-selo rg-selo-g ${v ? 'rg-selo-ok' : 'rg-selo-neutro'}`}>
                {v ? `ATIVA · v${String(v.versao)}` : 'SEM VERSÃO'}
              </span>
            )}
          </div>
          <div className="ac-subtitulo">
            {programa === 'vitamina'
              ? `${String(r.vitamina.produtos)} produtos no programa · a faixa de cada um é configurada em Produtos`
              : programa === 'paradao'
                ? SEGUE_O_PRE_VENCIDO
                : v
                  ? `Vigente desde ${diaMesAno(v.desde)} · ${v.motivo}`
                  : 'Nenhuma versão gravada para este programa'}
          </div>
        </div>
        <Fechar aoFechar={aoFechar} />
      </div>

      <div className="rg-corpo-drawer">
        <div className="rg-coluna">
          <section className="ac-secao">
            <div className="ac-secao-cabeca">
              <div className="ac-rotulo-mono ac-rotulo-mono-marca">ENTRADAS DA REGRA</div>
              <span className="rg-selo rg-selo-neutro rg-selo-borda">SÓ LEITURA</span>
              <span className="ac-espaco" />
              <span className="ac-nota">
                campos que o servidor aceita · sem construtor de fórmula
              </span>
            </div>
            {programa === 'vitamina' ? (
              <div className="ac-info">
                <Icone nome="money" tamanho={16} traco={1.6} />
                <span>
                  A vitamina paga por <b>faixa de preço configurada em cada produto</b>, com versão
                  própria por produto. Para ver ou mudar uma faixa, abra o produto em{' '}
                  <a href="#/produtos">Produtos</a>.
                </span>
              </div>
            ) : v ? (
              <div className="ac-grade-2 rg-entradas">
                <Caixa rotulo="Valor-base por venda" valor={r2(v.premioBase)} />
                <Caixa rotulo="Percentual sobre o aumento" valor={pct(v.pctAumento)} />
                <Caixa
                  rotulo="Teto por venda"
                  valor={tetoLegivel(v.teto)}
                  fraco={Number(v.teto) <= 0}
                />
                <Caixa rotulo="Origem" valor={origemLegivel(v.origem)} />
              </div>
            ) : (
              <div className="ac-info ac-info-fraco">Nenhum parâmetro gravado.</div>
            )}
            <div className="ac-info rg-motor">
              <IconeExtra nome="layers" tamanho={16} traco={1.5} />
              <span>
                O cálculo roda no servidor: ele devolve{' '}
                <b>preço usado, prêmio por unidade, total e a explicação</b>. A fórmula não vive
                espalhada nas telas.
              </span>
            </div>
          </section>

          {simula && <Simulador programa={programa} sessao={sessao} versao={v} />}

          {programa !== 'vitamina' && (
            <section className="ac-secao">
              <div className="ac-rotulo-mono">HISTÓRICO DE VERSÕES</div>
              <ListaDeVersoes versoes={versoes} />
              <div className="rg-pe">
                Nenhuma versão é apagada. Uma venda antiga continua explicada pela versão que valia
                no dia em que foi registrada.
              </div>
            </section>
          )}
        </div>

        <div className="rg-coluna rg-coluna-lado">
          <section className="rg-publicar">
            <div className="rg-publicar-cabeca">
              <div className="ac-modal-titulo">Publicar nova versão</div>
              <div className="ac-modal-sub">Vale a partir da próxima semana (sábado)</div>
            </div>
            <div className="rg-publicar-corpo">
              {v && (
                <div className="ac-bloco rg-publicar-bloco">
                  <div className="ac-linha-dado">
                    <span>Versão vigente</span>
                    <span>v{v.versao}</span>
                  </div>
                  <div className="ac-linha-dado">
                    <span>Vale desde</span>
                    <span>{diaMesAno(v.desde)}</span>
                  </div>
                  <div className="ac-linha-dado">
                    <span>Valor-base</span>
                    <span>{r2(v.premioBase)}</span>
                  </div>
                </div>
              )}
              {programa === 'pre_vencido' && publica && r.publicacao.permitida ? (
                <PublicarParametros
                  programa={programa}
                  v={v}
                  sessao={sessao}
                  valeDesde={r.publicacao.valeDesde}
                  porque={r.publicacao.porque}
                  aoPublicar={aoMudar}
                />
              ) : (
                <div className="ac-aviso">
                  <Icone nome="lock" tamanho={15} traco={1.7} />
                  <span>
                    {programa === 'vitamina'
                      ? 'A vitamina muda por produto, em Produtos.'
                      : programa === 'paradao'
                        ? SEGUE_O_PRE_VENCIDO
                        : 'Só quem administra as configurações publica uma versão nova.'}
                  </span>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </Veu>
  );
}

function Caixa({
  rotulo,
  valor,
  fraco = false,
}: {
  rotulo: string;
  valor: string;
  fraco?: boolean;
}) {
  return (
    <div>
      <div className="ac-campo-rotulo">{rotulo}</div>
      <div className={`rg-caixa${fraco ? ' rg-caixa-fraca' : ''}`}>{valor}</div>
    </div>
  );
}

function ListaDeVersoes({ versoes }: { versoes: readonly VersaoDeParametro[] }) {
  if (versoes.length === 0)
    return <div className="ac-info ac-info-fraco">Nenhuma versão gravada.</div>;
  return (
    <div className="rg-versoes">
      {versoes.map((v, i) => {
        const seguinte = versoes[i - 1];
        return (
          <div key={v.versao} className={`rg-versao${i === 0 ? ' rg-versao-vigente' : ''}`}>
            <span className="rg-versao-n">v{v.versao}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="rg-versao-titulo">
                {i === 0
                  ? `Ativa desde ${diaMesAno(v.desde)}`
                  : `${diaMes(v.desde)} → ${seguinte ? diaMesAno(new Date(new Date(seguinte.desde).getTime() - 1).toISOString()) : ''}`}
              </div>
              <div className="rg-versao-sub">
                Base {r2(v.premioBase)} · {pct(v.pctAumento)} · {tetoLegivel(v.teto)} · por{' '}
                {v.por ?? origemLegivel(v.origem)}
              </div>
              {v.motivo && <div className="rg-versao-motivo">“{v.motivo}”</div>}
            </div>
            {i === 0 ? (
              <span className="rg-status rg-status-ativa">Vigente</span>
            ) : (
              <span className="ac-nota rg-versao-nota">vendas do período usam esta</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DrawerDoHistorico({
  r,
  sessao,
  dono,
  aoMudar,
  aoFechar,
}: {
  r: RegrasVigentes;
  sessao: Sessao;
  dono: boolean;
  aoMudar: () => void;
  aoFechar: () => void;
}) {
  return (
    <Veu aoFechar={aoFechar} rotulo="Histórico de versões">
      <div className="ac-drawer-cabeca">
        <div style={{ flex: 1 }}>
          <div className="ac-drawer-nome">Histórico de versões</div>
          <div className="ac-subtitulo">
            Toda venda fica amarrada à versão que valia no momento. Nenhuma versão é apagada.
          </div>
        </div>
        <Fechar aoFechar={aoFechar} />
      </div>
      <section className="ac-secao">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">PRÉ-VENCIDOS</div>
        <ListaDeVersoes versoes={r.preVencido} />
      </section>
      <section className="ac-secao">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">PARADÃO</div>
        <div className="ac-info">
          <Icone nome="box" tamanho={16} traco={1.6} />
          <span>{SEGUE_O_PRE_VENCIDO}</span>
        </div>
      </section>
      <section className="ac-secao">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">VITAMINAS</div>
        <div className="ac-info">
          <Icone nome="money" tamanho={16} traco={1.6} />
          <span>
            Versão por produto · {r.vitamina.produtos} no programa
            {r.vitamina.ultimaMudanca
              ? ` · última mudança ${diaMesHora(r.vitamina.ultimaMudanca.em)} por ${r.vitamina.ultimaMudanca.por ?? 'sistema'}`
              : ''}
            . O histórico de cada produto fica em <a href="#/produtos">Produtos</a>.
          </span>
        </div>
      </section>
      <section className="ac-secao">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">META SEMANAL</div>
        <div className="ac-bloco">
          <div className="ac-linha-dado">
            <span>Itens por semana</span>
            <span>{r.metaSemanal.itens}</span>
          </div>
          <div className="ac-linha-dado">
            <span>Versão</span>
            <span>
              {r.metaSemanal.versao > 0
                ? `v${String(r.metaSemanal.versao)}${r.metaSemanal.desde ? ` · desde ${diaMesAno(r.metaSemanal.desde)}` : ''}`
                : 'padrão do sistema'}
            </span>
          </div>
          {r.metaSemanal.agendada && (
            <div className="ac-linha-dado">
              <span>A partir de {diaMesAno(r.metaSemanal.agendada.valeDesde)}</span>
              <span>{r.metaSemanal.agendada.itens} itens</span>
            </div>
          )}
        </div>
        {dono && <MudarMeta r={r} sessao={sessao} aoMudar={aoMudar} />}
      </section>
    </Veu>
  );
}

/* ------------------------------ simulador ------------------------------ */

function useBuscaDeProdutos(texto: string) {
  const [itens, setItens] = useState<readonly ProdutoComercial[] | null>(null);
  const [falhou, setFalhou] = useState(false);
  useEffect(() => {
    const t = texto.trim();
    if (t.length < 2) {
      setItens(null);
      return;
    }
    let vivo = true;
    const espera = window.setTimeout(() => {
      void listarProdutos({ filtro: 'todos', busca: t, grupo: '', pagina: 1 }).then((r) => {
        if (!vivo) return;
        setFalhou(!r.ok);
        setItens(r.ok ? r.dados.itens.slice(0, 8) : []);
      });
    }, 300);
    return () => {
      vivo = false;
      window.clearTimeout(espera);
    };
  }, [texto]);
  return { itens, falhou };
}

function Simulador({
  programa,
  sessao,
  versao,
}: {
  programa: Programa;
  sessao: Sessao;
  versao: VersaoDeParametro | undefined;
}) {
  const [busca, setBusca] = useState('');
  const [produto, setProduto] = useState<{ id: string; nome: string } | null>(null);
  const [faixa, setFaixa] = useState<'a' | 'b' | 'c'>('a');
  const [preco, setPreco] = useState('');
  const [qtd, setQtd] = useState('1');
  const [enviando, setEnviando] = useState(false);
  const [escrita, setEscrita] = useState<Escrita<SimulacaoDaRegra> | null>(null);
  const { itens, falhou } = useBuscaDeProdutos(produto ? '' : busca);
  const pedePreco = programa !== 'vitamina' || faixa === 'c';
  const precoOk = !pedePreco || lerReais(preco) !== null;
  const quantidade = Number(qtd);
  const pode =
    produto !== null && precoOk && Number.isInteger(quantidade) && quantidade >= 1 && !enviando;

  const simular = async () => {
    if (!produto) return;
    setEnviando(true);
    setEscrita(
      await simularRegra(sessao, {
        productId: produto.id,
        programa,
        faixa: programa === 'vitamina' ? faixa : 'pv',
        preco: pedePreco ? lerReais(preco) : null,
        quantidade,
      }),
    );
    setEnviando(false);
  };
  const res = escrita?.tipo === 'confirmada' ? escrita.dados : null;

  return (
    <section className="ac-secao">
      <div className="ac-secao-cabeca">
        <div className="ac-rotulo-mono ac-rotulo-mono-marca">SIMULAÇÃO</div>
        <span className="ac-espaco" />
        <span className="rg-selo rg-selo-info rg-selo-g">NÃO REGISTRA VENDA</span>
      </div>
      <div className={`rg-sim-campos${programa === 'vitamina' ? ' rg-sim-campos-4' : ''}`}>
        <div className="rg-sim-produto">
          <label className="ac-campo-rotulo" htmlFor="rg-sim-produto">
            Produto
          </label>
          {produto ? (
            <div className="rg-caixa rg-caixa-produto">
              <span className="rg-truncar">{produto.nome}</span>
              <button
                type="button"
                className="rg-trocar"
                aria-label="Trocar produto"
                onClick={() => {
                  setProduto(null);
                  setEscrita(null);
                }}
              >
                <Icone nome="x" tamanho={13} traco={1.8} />
              </button>
            </div>
          ) : (
            <input
              id="rg-sim-produto"
              className="rg-campo"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome, EAN ou código"
              autoComplete="off"
            />
          )}
          {!produto && itens && (
            <div className="rg-sugestoes" role="listbox" aria-label="Produtos encontrados">
              {falhou && <div className="rg-sugestao-vazia">Não foi possível buscar agora.</div>}
              {!falhou && itens.length === 0 && (
                <div className="rg-sugestao-vazia">Nenhum produto com esse nome.</div>
              )}
              {itens.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="rg-sugestao"
                  onClick={() => {
                    setProduto({ id: p.id, nome: p.descricao });
                    setBusca('');
                    setEscrita(null);
                  }}
                >
                  <span className="rg-truncar">{p.descricao}</span>
                  <span className="ac-mono ac-fraco">{p.codigo ?? p.ean ?? ''}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {programa === 'vitamina' && (
          <div>
            <label className="ac-campo-rotulo" htmlFor="rg-sim-faixa">
              Preço
            </label>
            <select
              id="rg-sim-faixa"
              className="rg-campo"
              value={faixa}
              onChange={(e) => setFaixa(e.target.value as 'a' | 'b' | 'c')}
            >
              <option value="a">Máximo (faixa A)</option>
              <option value="b">Mínimo (faixa B)</option>
              <option value="c">Outro valor</option>
            </select>
          </div>
        )}
        <div>
          <label className="ac-campo-rotulo" htmlFor="rg-sim-preco">
            Vendido por
          </label>
          <input
            id="rg-sim-preco"
            className={`rg-campo rg-campo-num${pedePreco && preco ? ' rg-campo-forte' : ''}`}
            value={pedePreco ? preco : 'da faixa'}
            disabled={!pedePreco}
            onChange={(e) => setPreco(e.target.value)}
            placeholder="R$ 0,00"
            inputMode="decimal"
            aria-invalid={pedePreco && preco !== '' && !precoOk}
          />
        </div>
        <div>
          <label className="ac-campo-rotulo" htmlFor="rg-sim-qtd">
            Quantidade
          </label>
          <input
            id="rg-sim-qtd"
            className="rg-campo rg-campo-num"
            value={qtd}
            onChange={(e) => setQtd(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric"
          />
        </div>
      </div>
      <div className="rg-sim-acoes">
        <button type="button" className="ac-botao" disabled={!pode} onClick={() => void simular()}>
          <IconeExtra nome="play" tamanho={12} traco={1.7} />
          {enviando ? 'Simulando…' : 'Simular no servidor'}
        </button>
        <span className="ac-nota">
          {versao ? `regra v${String(versao.versao)} vigente` : 'regra vigente'} · nada é gravado
        </span>
      </div>

      {escrita?.tipo === 'recusada' && (
        <div className="rg-resultado rg-resultado-recusa" role="alert">
          <div className="rg-resultado-rotulo">O SERVIDOR NÃO SIMULOU</div>
          <div className="rg-resultado-texto">{escrita.mensagem}</div>
        </div>
      )}
      {escrita?.tipo === 'incerta' && (
        <div className="rg-resultado rg-resultado-recusa" role="status">
          <div className="rg-resultado-rotulo">SEM RESPOSTA DO SERVIDOR</div>
          <div className="rg-resultado-texto">
            Tente de novo. Simulação nunca grava nada, então repetir é seguro.
          </div>
        </div>
      )}
      {res && (
        <div className="rg-resultado" aria-live="polite">
          <div className="rg-resultado-linha">
            <div className="rg-resultado-rotulo">RESULTADO DE SIMULAÇÃO</div>
            <span className="ac-espaco" />
            <span className="rg-resultado-fonte">calculado no servidor</span>
          </div>
          <div className="rg-resultado-valor">{reais(res.premioTotal)}</div>
          <div className="rg-resultado-sub">
            {reais(res.premioUnitario)} por unidade · vendido por {reais(res.precoUnitario)} ·{' '}
            {quantidade} {quantidade === 1 ? 'unidade' : 'unidades'}
          </div>
          {typeof res.bloqueio === 'string' && res.bloqueio && (
            <div className="rg-resultado-bloqueio">
              <Icone nome="warn" tamanho={14} traco={1.7} />
              <span>
                <b>Esse preço não pode ser registrado.</b> {res.bloqueio}
              </span>
            </div>
          )}
          <div className="rg-resultado-rotulo rg-resultado-como">COMO FOI CALCULADO</div>
          <div className="rg-resultado-texto">
            {res.explicacao || 'O servidor não devolveu explicação para esta conta.'}
          </div>
          <div className="rg-resultado-pe">
            Nenhuma venda foi criada. O valor serve para conferir a regra.
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------ detalhe da campanha ------------------------------ */

const EVENTO: Readonly<Record<string, string>> = {
  criada: 'criou o rascunho',
  editada: 'editou o rascunho',
  publicada: 'publicou',
  pausada: 'pausou',
  retomada: 'retomou',
  cancelada: 'cancelou',
};

type Acao = 'publicar' | 'pausar' | 'retomar' | 'cancelar';

function DrawerDaCampanha({
  id,
  sessao,
  gerir,
  aoFechar,
  aoEditar,
  aoMudar,
}: {
  id: string;
  sessao: Sessao;
  gerir: boolean;
  aoFechar: () => void;
  aoEditar: (id: string) => void;
  aoMudar: () => void;
}) {
  const [carga, setCarga] = useState<Carga<DetalheDaCampanha>>({ fase: 'carregando' });
  const [acao, setAcao] = useState<Acao | null>(null);
  const carregar = useCallback(async () => {
    const r = await lerCampanha(id);
    setCarga(r.ok ? { fase: 'pronto', dados: r.dados } : { fase: 'erro', falha: r.falha });
  }, [id]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const c = carga.fase === 'pronto' ? carga.dados : null;
  const passada = c?.estado === 'ENCERRADA' || c?.estado === 'CANCELADA';
  const podePausar = c?.estado === 'ATIVA' || c?.estado === 'PROGRAMADA';
  const podeCancelar = c !== null && !passada;

  return (
    <Veu aoFechar={aoFechar} rotulo={c ? c.nome : 'Campanha'}>
      <div className="ac-drawer-cabeca">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="rg-drawer-titulo">
            <span className="ac-drawer-nome">{c ? c.nome : 'Campanha'}</span>
            {c && <StatusDaCampanha c={c} />}
          </div>
          {c && (
            <div className="ac-drawer-meta">
              <span>
                <Icone nome="cal" tamanho={13} traco={1.6} />{' '}
                {c.inicio && c.fim
                  ? `${diaMesHora(c.inicio)} → ${diaMesHora(c.fim)}`
                  : 'período a definir'}
              </span>
              <span>{c.produtos} produtos</span>
              <span>{PUBLICO[c.publico]}</span>
              {/* sem autor para quem só vê campanhas (o servidor não manda) */}
              {c.responsavel && <span>por {c.responsavel}</span>}
            </div>
          )}
        </div>
        <Fechar aoFechar={aoFechar} />
      </div>
      {carga.fase === 'carregando' && <Esqueleto linhas={4} />}
      {carga.fase === 'erro' && (
        <div style={{ padding: 18 }}>
          <ErroDaLista
            titulo={
              carga.falha.tipo === 'inesperado'
                ? 'Não foi possível abrir a campanha'
                : 'Campanha não encontrada'
            }
            texto="A lista continua disponível."
            aoTentar={() => void carregar()}
          />
        </div>
      )}
      {c && (
        <>
          <section className="ac-secao">
            {c.descricao && <p className="rg-descricao">{c.descricao}</p>}
            <div className="ac-info">
              <IconeExtra nome="layers" tamanho={16} traco={1.5} />
              <span>
                <b>Campanha é camada temporária</b> sobre produtos que já existem e{' '}
                <b>não altera a premiação</b>: o que é pago continua vindo das regras vigentes.
                {c.estado === 'RASCUNHO'
                  ? ' Rascunho não aparece para o balconista.'
                  : ' Publicada não se edita — pause ou cancele com motivo.'}
              </span>
            </div>
          </section>

          {gerir && !passada && (
            <section className="ac-secao">
              <div className="ac-rotulo-mono">AÇÕES</div>
              <div className="rg-acoes-drawer">
                {c.estado === 'RASCUNHO' && (
                  <>
                    <button
                      type="button"
                      className="ac-botao ac-botao-primario ac-botao-g"
                      onClick={() => setAcao('publicar')}
                    >
                      Publicar campanha
                    </button>
                    <button
                      type="button"
                      className="ac-botao ac-botao-g"
                      onClick={() => aoEditar(c.id)}
                    >
                      Editar rascunho
                    </button>
                  </>
                )}
                {podePausar && (
                  <button
                    type="button"
                    className="ac-botao ac-botao-g"
                    onClick={() => setAcao('pausar')}
                  >
                    <IconeExtra nome="pause" tamanho={13} />
                    Pausar
                  </button>
                )}
                {c.estado === 'PAUSADA' && (
                  <button
                    type="button"
                    className="ac-botao ac-botao-g"
                    onClick={() => setAcao('retomar')}
                  >
                    <IconeExtra nome="play" tamanho={13} />
                    Retomar
                  </button>
                )}
                {podeCancelar && (
                  <button
                    type="button"
                    className="ac-botao ac-botao-perigo ac-botao-g"
                    onClick={() => setAcao('cancelar')}
                  >
                    Cancelar campanha
                  </button>
                )}
              </div>
              <div className="rg-pe">
                Pausar e cancelar exigem motivo · vendas já registradas continuam válidas · o
                histórico permanece.
              </div>
            </section>
          )}

          <section className="ac-secao">
            <div className="ac-secao-cabeca">
              <div className="ac-rotulo-mono">PRODUTOS PARTICIPANTES</div>
              <span className="ac-espaco" />
              <span className="rg-contador">{c.itens.length} selecionados</span>
            </div>
            {c.itens.length === 0 ? (
              <div className="ac-info ac-info-fraco">
                Nenhum produto escolhido — é preciso ao menos um para publicar.
              </div>
            ) : (
              <div className="rg-produtos">
                {c.itens.map((p) => (
                  <div key={p.productId} className="rg-produto">
                    <span className="rg-truncar">{p.nome}</span>
                    <span className="ac-mono ac-fraco">{p.codigo ?? ''}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="ac-secao">
            <div className="ac-rotulo-mono">EVENTOS</div>
            {c.eventos.length === 0 && <div className="ac-nota">Nenhum evento registrado.</div>}
            {c.eventos.map((e, i) => (
              <div key={`${e.em}-${String(i)}`} className="ac-historico">
                <span className="ac-historico-hora">{diaMesHora(e.em)}</span>
                <div style={{ flex: 1 }}>
                  <b>{e.por ?? 'Sistema'}</b> {EVENTO[e.tipo] ?? e.tipo}
                  {e.motivo && <div className="rg-versao-motivo">Motivo: {e.motivo}</div>}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
      {acao && c && (
        <ModalDeMotivo
          titulo={TITULO_DA_ACAO[acao](c.nome)}
          texto={TEXTO_DA_ACAO[acao]}
          botao={BOTAO_DA_ACAO[acao]}
          pedeMotivo={acao === 'pausar' || acao === 'cancelar'}
          perigo={acao === 'cancelar'}
          aoFechar={() => setAcao(null)}
          aoConfirmar={(motivo) => mudarCampanha(sessao, c.id, acao, c.versao, motivo)}
          aoConcluir={() => {
            setAcao(null);
            aoMudar();
            void carregar();
          }}
        />
      )}
    </Veu>
  );
}

const TITULO_DA_ACAO: Readonly<Record<Acao, (n: string) => string>> = {
  publicar: (n) => `Publicar ${n}`,
  pausar: (n) => `Pausar ${n}`,
  retomar: (n) => `Retomar ${n}`,
  cancelar: (n) => `Cancelar ${n}`,
};
const TEXTO_DA_ACAO: Readonly<Record<Acao, string>> = {
  publicar:
    'Vale só dentro do período definido. Depois de publicada, a campanha não se edita — só pausa ou cancela. A premiação não muda.',
  pausar:
    'Vendas já registradas continuam válidas. A campanha deixa de valer a partir do momento definido pelo servidor.',
  retomar: 'A campanha volta a valer dentro do período que já estava definido.',
  cancelar:
    'Cancelar é definitivo. Vendas já registradas continuam válidas e o histórico permanece.',
};
const BOTAO_DA_ACAO: Readonly<Record<Acao, string>> = {
  publicar: 'Publicar',
  pausar: 'Confirmar pausa',
  retomar: 'Retomar',
  cancelar: 'Cancelar campanha',
};

function ModalDeMotivo<T>({
  titulo,
  texto,
  botao,
  pedeMotivo = true,
  perigo = false,
  aoFechar,
  aoConfirmar,
  aoConcluir,
}: {
  titulo: string;
  texto: string;
  botao: string;
  pedeMotivo?: boolean;
  perigo?: boolean;
  aoFechar: () => void;
  aoConfirmar: (motivo: string | null) => Promise<Escrita<T>>;
  aoConcluir: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [escrita, setEscrita] = useState<Escrita<T> | null>(null);
  useEscape(aoFechar, !enviando);
  const valido = !pedeMotivo || motivo.trim().length >= 5;
  const confirmar = async () => {
    setEnviando(true);
    const r = await aoConfirmar(pedeMotivo ? motivo.trim() : null);
    setEnviando(false);
    if (r.tipo === 'confirmada') aoConcluir();
    else setEscrita(r);
  };
  return (
    <div
      className="ac-veu ac-veu-centro rg-veu-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !enviando) aoFechar();
      }}
    >
      <div
        className={`ac-modal${perigo ? ' ac-modal-perigo' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div className="ac-modal-cabeca">
          <div className="ac-modal-titulo">{titulo}</div>
        </div>
        <div className="ac-modal-corpo">
          <p className="rg-modal-texto">{texto}</p>
          {pedeMotivo && (
            <>
              <label className="ac-campo-rotulo" htmlFor="rg-motivo">
                Motivo — obrigatório
              </label>
              <textarea
                id="rg-motivo"
                className="ac-texto-area"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={500}
                placeholder="Mínimo 5 caracteres. Fica registrado com seu nome e a hora."
                autoFocus
              />
            </>
          )}
          <Desfecho
            escrita={escrita}
            incerta="Pode ter sido aplicado. Feche e confira o estado da campanha antes de repetir."
          />
          <div className="ac-botoes-modal">
            <button
              type="button"
              className="ac-botao-cheio"
              disabled={!valido || enviando || escrita?.tipo === 'incerta'}
              onClick={() => void confirmar()}
            >
              {enviando ? 'Enviando…' : botao}
            </button>
            <button type="button" className="ac-botao-contorno" onClick={aoFechar}>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ nova campanha / editar rascunho ------------------------------ */

const PASSOS = ['Informações', 'Produtos', 'Público e período', 'Revisão'] as const;

function FormularioDaCampanha({
  id,
  sessao,
  aoFechar,
  aoSalvar,
  aoConferir,
}: {
  id: string | null;
  sessao: Sessao;
  aoFechar: () => void;
  aoSalvar: (id: string) => void;
  aoConferir: () => void;
}) {
  const [passo, setPasso] = useState(0);
  const [versao, setVersao] = useState<number | null>(null);
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [publico, setPublico] = useState<PublicoDaCampanha>('BALCONISTAS');
  const [escolhidos, setEscolhidos] = useState<
    readonly { id: string; nome: string; codigo: string | null }[]
  >([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(id !== null);
  const [enviando, setEnviando] = useState(false);
  const [escrita, setEscrita] = useState<Escrita<{ id: string; versao: number }> | null>(null);
  const { itens, falhou } = useBuscaDeProdutos(busca);

  useEffect(() => {
    if (!id) return;
    void lerCampanha(id).then((r) => {
      setCarregando(false);
      if (!r.ok) return;
      const c = r.dados;
      setVersao(c.versao);
      setNome(c.nome);
      setDescricao(c.descricao ?? '');
      setInicio(paraCampoDataHora(c.inicio));
      setFim(paraCampoDataHora(c.fim));
      setPublico(c.publico);
      setEscolhidos(c.itens.map((p) => ({ id: p.productId, nome: p.nome, codigo: p.codigo })));
    });
  }, [id]);

  const nomeOk = nome.trim().length >= 3 && nome.trim().length <= 80;
  const periodoOk = !inicio || !fim || fim > inicio;
  const marcado = (pid: string) => escolhidos.some((e) => e.id === pid);
  const alternar = (p: { id: string; nome: string; codigo: string | null }) =>
    setEscolhidos((l) =>
      l.some((e) => e.id === p.id) ? l.filter((e) => e.id !== p.id) : [...l, p],
    );

  const salvar = async () => {
    setEnviando(true);
    const r = await salvarCampanha(sessao, {
      id,
      versao,
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      inicio: deCampoDataHora(inicio),
      fim: deCampoDataHora(fim),
      publico,
      productIds: escolhidos.map((e) => e.id),
    });
    setEnviando(false);
    if (r.tipo === 'confirmada') aoSalvar(r.dados.id);
    else setEscrita(r);
  };

  const lista = busca.trim().length >= 2 ? itens : null;
  // Sem busca, a lista mostra o que já foi escolhido (para desmarcar sem procurar de novo).
  const linhas: readonly {
    id: string;
    nome: string;
    codigo: string | null;
    fabricante: string | null;
  }[] = lista
    ? lista.map((p) => ({
        id: p.id,
        nome: p.descricao,
        codigo: p.codigo,
        fabricante: p.fabricante,
      }))
    : escolhidos.map((e) => ({ ...e, fabricante: null }));

  return (
    <Veu aoFechar={aoFechar} rotulo={id ? 'Editar rascunho' : 'Nova campanha'}>
      <div className="ac-drawer-cabeca">
        <div style={{ flex: 1 }}>
          <div className="ac-drawer-nome">{id ? 'Editar rascunho' : 'Nova campanha'}</div>
          <div className="ac-subtitulo">
            {nome.trim() || 'Sem nome'}
            {inicio && fim
              ? ` · ${periodoCurto(deCampoDataHora(inicio), deCampoDataHora(fim))}`
              : ''}{' '}
            · rascunho não afeta venda nem aparece para o balconista
          </div>
        </div>
        <Fechar aoFechar={aoFechar} />
      </div>
      <ol className="rg-passos" aria-label="Etapas">
        {PASSOS.map((p, i) => (
          <li
            key={p}
            className={i < passo ? 'rg-passo-feito' : i === passo ? 'rg-passo-atual' : undefined}
            aria-current={i === passo ? 'step' : undefined}
          >
            <button type="button" onClick={() => (nomeOk || i === 0) && setPasso(i)}>
              {i + 1} · {p}
            </button>
          </li>
        ))}
      </ol>

      {carregando ? (
        <Esqueleto linhas={4} />
      ) : (
        <div className="ac-secao rg-form">
          {passo === 0 && (
            <>
              <label className="ac-campo-rotulo" htmlFor="rg-nome">
                Nome da campanha
              </label>
              <input
                id="rg-nome"
                className="rg-campo rg-campo-titulo"
                value={nome}
                maxLength={80}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Semana de vitaminas"
                autoFocus
              />
              <label className="ac-campo-rotulo" htmlFor="rg-descricao">
                Descrição (opcional)
              </label>
              <textarea
                id="rg-descricao"
                className="ac-texto-area"
                value={descricao}
                maxLength={500}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="O que o balconista precisa saber sobre a campanha"
              />
              <div className="ac-info">
                <IconeExtra nome="layers" tamanho={16} traco={1.5} />
                <span>
                  Campanha é camada temporária sobre produtos existentes e{' '}
                  <b>não altera a premiação</b>. Nenhum cadastro é duplicado.
                </span>
              </div>
            </>
          )}

          {passo === 1 && (
            <>
              <div className="ac-secao-cabeca">
                <div className="rg-form-titulo">Produtos participantes</div>
                <span className="ac-espaco" />
                <span className="rg-contador rg-contador-escuro">
                  {escolhidos.length} {escolhidos.length === 1 ? 'selecionado' : 'selecionados'}
                </span>
              </div>
              <label className="ac-busca rg-busca">
                <Icone nome="search" tamanho={14} traco={1.6} />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Produto, EAN ou fabricante"
                  aria-label="Buscar produtos"
                  autoFocus
                />
              </label>
              <div className="rg-escolha">
                {lista === null && escolhidos.length === 0 && (
                  <div className="rg-sugestao-vazia">
                    Busque pelo nome, EAN ou fabricante para escolher os produtos.
                  </div>
                )}
                {lista !== null && falhou && (
                  <div className="rg-sugestao-vazia">Não foi possível buscar agora.</div>
                )}
                {lista !== null && !falhou && lista.length === 0 && (
                  <div className="rg-sugestao-vazia">Nenhum produto com esse nome.</div>
                )}
                {linhas.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="rg-escolha-linha"
                    aria-pressed={marcado(p.id)}
                    onClick={() => alternar({ id: p.id, nome: p.nome, codigo: p.codigo })}
                  >
                    <span className={`ac-caixinha${marcado(p.id) ? ' ac-caixinha-marcada' : ''}`}>
                      {marcado(p.id) && <Icone nome="check" tamanho={12} traco={2.2} />}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span className="rg-escolha-nome">{p.nome}</span>
                      <span className="rg-escolha-sub">
                        {[p.codigo, p.fabricante].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {passo === 2 && (
            <>
              <div className="ac-campo-rotulo">Público</div>
              <div className="rg-publico">
                {(Object.keys(PUBLICO) as PublicoDaCampanha[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={publico === p}
                    className={`rg-pilula${publico === p ? ' rg-pilula-ativa' : ''}`}
                    onClick={() => setPublico(p)}
                  >
                    {PUBLICO[p]}
                  </button>
                ))}
              </div>
              <div className="ac-grade-2">
                <div>
                  <label className="ac-campo-rotulo" htmlFor="rg-inicio">
                    Começa em (Brasília)
                  </label>
                  <input
                    id="rg-inicio"
                    type="datetime-local"
                    className="rg-campo"
                    value={inicio}
                    onChange={(e) => setInicio(e.target.value)}
                  />
                </div>
                <div>
                  <label className="ac-campo-rotulo" htmlFor="rg-fim">
                    Termina em (Brasília)
                  </label>
                  <input
                    id="rg-fim"
                    type="datetime-local"
                    className="rg-campo"
                    value={fim}
                    onChange={(e) => setFim(e.target.value)}
                    aria-invalid={!periodoOk}
                  />
                </div>
              </div>
              {!periodoOk && (
                <div className="rm-desfecho rm-recusada">
                  <Icone nome="x" tamanho={14} traco={1.8} />
                  <span>O fim precisa vir depois do início.</span>
                </div>
              )}
              <div className="ac-aviso">
                <Icone nome="warn" tamanho={15} traco={1.7} />
                <span>
                  Vale só para vendas dentro do período. Nada do que já foi registrado é
                  recalculado. Para publicar, o período precisa estar definido.
                </span>
              </div>
            </>
          )}

          {passo === 3 && (
            <div className="ac-bloco">
              <div className="ac-linha-dado">
                <span>Nome</span>
                <span>{nome.trim() || '—'}</span>
              </div>
              <div className="ac-linha-dado">
                <span>Produtos</span>
                <span>{escolhidos.length}</span>
              </div>
              <div className="ac-linha-dado">
                <span>Público</span>
                <span>{PUBLICO[publico]}</span>
              </div>
              <div className="ac-linha-dado">
                <span>Período</span>
                <span>
                  {inicio && fim
                    ? `${diaMesHora(deCampoDataHora(inicio) ?? '')} → ${diaMesHora(deCampoDataHora(fim) ?? '')}`
                    : 'a definir'}
                </span>
              </div>
              <div className="ac-linha-dado">
                <span>Premiação</span>
                <span>não muda</span>
              </div>
              <div className="ac-pe-bloco">
                Salvar deixa como <b>rascunho</b>. Publicar é um passo à parte, no detalhe da
                campanha, e o servidor confere período e produtos antes.
              </div>
            </div>
          )}

          <Desfecho
            escrita={escrita}
            incerta="O rascunho pode ter sido salvo. Confira a lista antes de salvar de novo."
            aoConferir={aoConferir}
          />
          <div className="rg-form-acoes">
            {passo < PASSOS.length - 1 ? (
              <button
                type="button"
                className="ac-botao ac-botao-primario ac-botao-g"
                disabled={!nomeOk || (passo === 2 && !periodoOk)}
                onClick={() => setPasso((p) => p + 1)}
              >
                Continuar para {PASSOS[passo + 1]?.toLowerCase()}
              </button>
            ) : (
              <button
                type="button"
                className="ac-botao ac-botao-primario ac-botao-g"
                disabled={!nomeOk || !periodoOk || enviando || escrita?.tipo === 'incerta'}
                onClick={() => void salvar()}
              >
                {enviando ? 'Salvando…' : 'Salvar rascunho'}
              </button>
            )}
            <button
              type="button"
              className="ac-botao ac-botao-g"
              onClick={() => (passo === 0 ? aoFechar() : setPasso((p) => p - 1))}
            >
              Voltar
            </button>
            <span className="ac-espaco" />
            <span className="ac-nota">Rascunho não afeta venda nem aparece para o balconista</span>
          </div>
        </div>
      )}
    </Veu>
  );
}

/** Nova versão dos parâmetros: valor-base, % do aumento e teto — com motivo, da próxima semana. */
function PublicarParametros({
  programa,
  v,
  sessao,
  valeDesde,
  porque,
  aoPublicar,
}: {
  programa: 'pre_vencido' | 'paradao';
  v: VersaoDeParametro | undefined;
  sessao: Sessao;
  valeDesde: string;
  porque: string;
  aoPublicar: () => void;
}) {
  const br = (x: string | undefined) =>
    x === undefined ? '' : String(Number(x)).replace('.', ',');
  const [base, setBase] = useState(br(v?.premioBase));
  const [pct, setPct] = useState(br(v?.pctAumento));
  const [teto, setTeto] = useState(br(v?.teto));
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const publicar = async () => {
    setEnviando(true);
    setMsg(null);
    const r = await publicarParametros(sessao, {
      programa,
      premioBase: base,
      pctAumento: pct,
      teto,
      motivo,
      versaoAtual: v?.versao ?? 0,
    });
    setEnviando(false);
    if (r.tipo === 'confirmada') {
      setMsg({
        ok: true,
        texto: `Versão ${String(r.dados.versao)} publicada — vale a partir de ${diaMesAno(r.dados.valeDesde)}.`,
      });
      setMotivo('');
      aoPublicar();
    } else
      setMsg({
        ok: false,
        texto:
          r.tipo === 'recusada'
            ? r.mensagem
            : 'Não deu para confirmar se publicou. Recarregue e confira o histórico de versões.',
      });
  };
  return (
    <div className="rg-publicar-form">
      <label>
        <span>Valor-base por venda (R$)</span>
        <input inputMode="decimal" value={base} onChange={(e) => setBase(e.target.value)} />
      </label>
      <label>
        <span>Percentual sobre o aumento (%)</span>
        <input inputMode="decimal" value={pct} onChange={(e) => setPct(e.target.value)} />
      </label>
      <label>
        <span>Teto por venda (R$)</span>
        <input inputMode="decimal" value={teto} onChange={(e) => setTeto(e.target.value)} />
      </label>
      <label>
        <span>Motivo (fica no histórico)</span>
        <input value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      <p className="rg-publicar-nota">
        Vale a partir de <b>{diaMesAno(valeDesde)}</b> — {porque}.
      </p>
      <button
        type="button"
        className="ac-botao-cheio rg-publicar-botao"
        disabled={enviando || motivo.trim().length < 5 || !base || !pct || !teto}
        onClick={() => void publicar()}
      >
        {enviando ? 'Publicando…' : 'Publicar nova versão'}
      </button>
      {msg && (
        <p className={msg.ok ? 'rg-publicar-ok' : 'rg-publicar-erro'} role="status">
          {msg.texto}
        </p>
      )}
    </div>
  );
}

/** Só os donos: meta nova para todos, com motivo — vale da próxima semana (sábado). */
function MudarMeta({
  r,
  sessao,
  aoMudar,
}: {
  r: RegrasVigentes;
  sessao: Sessao;
  aoMudar: () => void;
}) {
  const [itens, setItens] = useState(String(r.metaSemanal.agendada?.itens ?? r.metaSemanal.itens));
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);
  const salvar = async () => {
    setEnviando(true);
    setMsg(null);
    const x = await salvarMetaSemanal(sessao, {
      itens: Number(itens),
      motivo,
      versaoAtual: r.metaSemanal.versao,
    });
    setEnviando(false);
    if (x.tipo === 'confirmada') {
      setMsg({ ok: true, texto: `Meta salva — vale a partir de ${diaMesAno(x.dados.valeDesde)}.` });
      setMotivo('');
      aoMudar();
    } else
      setMsg({
        ok: false,
        texto: x.tipo === 'recusada' ? x.mensagem : 'Não deu para confirmar. Recarregue e confira.',
      });
  };
  return (
    <div className="rg-publicar-form" style={{ marginTop: 12 }}>
      <label>
        <span>Nova meta (itens por semana, para todos)</span>
        <input
          inputMode="numeric"
          value={itens}
          onChange={(e) => setItens(e.target.value.replace(/\D/g, '').slice(0, 3))}
        />
      </label>
      <label>
        <span>Motivo (fica no histórico)</span>
        <input value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      <p className="rg-publicar-nota">
        Vale a partir do próximo sábado — a semana em andamento continua com a meta atual.
      </p>
      <button
        type="button"
        className="ac-botao-cheio rg-publicar-botao"
        disabled={enviando || itens === '' || motivo.trim().length < 5}
        onClick={() => void salvar()}
      >
        {enviando ? 'Salvando…' : 'Salvar meta'}
      </button>
      {msg && (
        <p className={msg.ok ? 'rg-publicar-ok' : 'rg-publicar-erro'} role="status">
          {msg.texto}
        </p>
      )}
    </div>
  );
}
